import { useState, useEffect, useCallback } from 'react';
import { Project } from '../types/project';
import { Tooltip } from './Tooltip';
import { SERVICE_CONFIGURATIONS, resolveServiceConfiguration } from '../lib/serviceConfig';
import {
  fetchUtilitiesForZip,
  fetchUtilitiesForState,
  searchUtilitiesByName,
  useUtilityDetail,
} from '../hooks/useUtility';
import { type UtilityListItem } from '../types/utility';
import { UtilityCombobox, OTHER_VALUE } from './UtilityCombobox';
import { UtilityNameSuggest } from './UtilityNameSuggest';
import { stateCodeOf, sanitizeSearchTerm, filterUtilities } from '../lib/utilityPicker';

// Static fallback used when the utilities table is empty or unavailable
const FALLBACK_UTILITIES: UtilityListItem[] = [
  { id: 'gmp', name: 'Green Mountain Power', state: 'VT', states_served: null, ami_contact_email: 'energyinnovationteam@greenmountainpower.com', ami_status: 'email_request', residential_customers: null },
  { id: 'bed', name: 'Burlington Electric Department', state: 'VT', states_served: null, ami_contact_email: 'engineering@burlingtonelectric.com', ami_status: 'customer_request', residential_customers: null },
];

interface ServiceInfoFormProps {
  project: Partial<Project>;
  onChange: (updates: Partial<Project>) => void;
  isReadOnly?: boolean;
  showRequiredHighlights?: boolean;
}

export function ServiceInfoForm({ project, onChange, isReadOnly = false, showRequiredHighlights = false }: ServiceInfoFormProps) {
  // The likely answers only — the utilities serving this ZIP, or failing that
  // the state's. The whole table is never held here; searching reaches the rest.
  const [likely, setLikely] = useState<UtilityListItem[]>([]);
  const [likelyLabel, setLikelyLabel] = useState('Start typing to search');
  const [loadingUtilities, setLoadingUtilities] = useState(false);
  const [offlineList, setOfflineList] = useState<UtilityListItem[] | null>(null);
  // The full row for the chosen utility feeds the notes line under the
  // dropdown. The dropdown itself only ever holds the light list. Everything
  // about getting the data (routes, steps, demand capture) lives on the Get
  // data page of the Existing Load tab, not here.
  const { utility: selectedDetail } = useUtilityDetail(project.utility_id);

  // selectedValue is the UUID of the chosen utility, 'other', or '' (unset)
  const [selectedValue, setSelectedValue] = useState<string>(() => {
    if (project.utility_id) return project.utility_id;
    if (project.ami_data_provider && !project.utility_id) return 'other';
    return '';
  });

  const projectState = stateCodeOf(project.customer_state);

  useEffect(() => {
    fetchLikely(project.customer_zip, projectState);
  }, [project.customer_zip, projectState]);

  // Keep selectedValue in sync if project loads after initial render
  useEffect(() => {
    if (project.utility_id) {
      setSelectedValue(project.utility_id);
    }
  }, [project.utility_id]);

  // The selected configuration is derived from what the project stores rather
  // than held in local state, so it stays correct when a project loads late.
  const selectedConfiguration = resolveServiceConfiguration(
    project.service_voltage_numeric,
    project.service_phase
  );

  /** One selection writes all three stored fields, so they cannot disagree. */
  function handleConfigurationChange(label: string) {
    const config = SERVICE_CONFIGURATIONS.find((c) => c.label === label);
    if (!config) return;
    onChange({
      service_voltage_text: config.label,
      service_voltage_numeric: config.volts,
      service_phase: config.phase,
    });
  }

  /**
   * The likely answers, cheaply. The ZIP's utilities when coverage knows them,
   * the state's otherwise, and nothing at all when neither is set yet — in
   * which case the picker simply invites a search. Never the whole table: it
   * is ~3,400 rows and this runs on every visit to the tab.
   */
  async function fetchLikely(zip: string | undefined, state: string | null) {
    setLoadingUtilities(true);
    try {
      if (zip && /^\d{5}$/.test(zip)) {
        try {
          const rows = await fetchUtilitiesForZip(zip);
          if (rows.length > 0) {
            setLikely(rows);
            setLikelyLabel(`Serves ZIP ${zip}`);
            return;
          }
        } catch (error) {
          // Fail open: a coverage failure falls through to the state list
          // rather than leaving the contractor with nothing.
          console.error('Error fetching ZIP coverage:', error);
        }
      }
      if (state) {
        try {
          const rows = await fetchUtilitiesForState(state);
          setLikely(rows);
          setLikelyLabel(
            zip && /^\d{5}$/.test(zip)
              ? `No coverage recorded for ${zip} — utilities in ${state}`
              : `Utilities in ${state}`
          );
          return;
        } catch (error) {
          console.error('Error fetching utilities for state:', error);
        }
      }
      setLikely([]);
      setLikelyLabel('Start typing to search every utility');
    } finally {
      setLoadingUtilities(false);
    }
  }

  /**
   * Name search across the table. If the network is refusing entirely, fall
   * back to the two hard-coded Vermont rows so the form still works offline;
   * that is what FALLBACK_UTILITIES has always been for.
   */
  const runSearch = useCallback(async (term: string): Promise<UtilityListItem[]> => {
    const clean = sanitizeSearchTerm(term);
    if (!clean) return [];
    try {
      return await searchUtilitiesByName(clean);
    } catch (error) {
      console.error('Error searching utilities:', error);
      const local = offlineList ?? FALLBACK_UTILITIES;
      setOfflineList(local);
      return filterUtilities(local, clean);
    }
  }, [offlineList]);

  function handleUtilityChange(value: string, utility?: UtilityListItem) {
    setSelectedValue(value);
    if (value === '') {
      onChange({ utility_id: null, ami_data_provider: '' });
    } else if (value === OTHER_VALUE) {
      onChange({ utility_id: null, ami_data_provider: '' });
    } else if (utility) {
      setPickedName(utility.name);
      onChange({ utility_id: utility.id, ami_data_provider: utility.name });
    }
  }

  /**
   * The selected utility's name. The picker holds only the likely list, so a
   * project that already names a utility outside it (a different state, a
   * search result chosen last session) still shows its name: the detail row
   * carries it, and pickedName covers the instant before that arrives.
   */
  const [pickedName, setPickedName] = useState<string | null>(null);
  const selectedUtilityName =
    selectedValue && selectedValue !== OTHER_VALUE
      ? selectedDetail?.name
        ?? likely.find((u) => u.id === selectedValue)?.name
        ?? pickedName
        ?? project.ami_data_provider
        ?? null
      : null;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Existing Electrical Service
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Configuration <span className="text-red-600">*</span>
              <Tooltip text="The voltage and phase of the existing service, from the panel label or the meter. Nearly all homes are 120/240V single-phase. Choose a three-phase option only for a commercial service that genuinely is three-phase — it changes how service capacity is calculated." />
            </label>
            <select
              value={selectedConfiguration.label}
              onChange={(e) => handleConfigurationChange(e.target.value)}
              data-testid="service-configuration"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isReadOnly}
            >
              {SERVICE_CONFIGURATIONS.map((c) => (
                <option key={c.label} value={c.label}>
                  {c.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {selectedConfiguration.phase === 'three'
                ? `Capacity calculated as √3 × ${selectedConfiguration.volts}V × amps.`
                : `Capacity calculated as ${selectedConfiguration.volts}V × amps.`}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Rating (Main Breaker){' '}
              <span className="text-red-600">*</span>
              <Tooltip text="Found on the main breaker inside the panel. Common values: 100, 150, 200 amps." />
              {showRequiredHighlights && (!project.service_rating_amps || project.service_rating_amps <= 0) && <span className="text-red-600 text-xs ml-1">Required</span>}
            </label>
            <input
              type="number"
              value={project.service_rating_amps || ''}
              onChange={(e) =>
                onChange({ service_rating_amps: Number(e.target.value) })
              }
              placeholder="Amps"
              min="0"
              step="1"
              required
              data-testid="service-rating-amps"
              className={showRequiredHighlights && (!project.service_rating_amps || project.service_rating_amps <= 0)
                ? 'w-full px-3 py-2 border border-red-500 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed'
                : 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed'}
              disabled={isReadOnly}
            />
            <p className="text-xs text-gray-500 mt-1">
              Example: 100, 150, 200, 400
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Utility Provider
        </h2>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Utility Provider <span className="text-red-600">*</span> <Tooltip text="Select the utility that provides electric service to this address. It decides how TrueLoad gets the interval data: the customer's own download, an email request, or a recording on site. Confirm with the customer if unsure — it's on their electric bill." />
              {likely.length > 0 && (
                <span className="ml-2 text-xs text-gray-500 font-normal">
                  (serving this ZIP first — type to search anywhere)
                </span>
              )}
              {showRequiredHighlights && !project.utility_id && !project.ami_data_provider?.trim() && <span className="text-red-600 text-xs ml-1">Required</span>}
            </label>
            <UtilityCombobox
              value={selectedValue}
              selectedName={selectedUtilityName}
              likely={likely}
              likelyLabel={likelyLabel}
              projectState={projectState}
              loading={loadingUtilities}
              disabled={isReadOnly}
              invalid={Boolean(showRequiredHighlights && !project.utility_id && !project.ami_data_provider?.trim())}
              onSelect={handleUtilityChange}
              onSearch={runSearch}
            />
            {loadingUtilities && (
              <p className="text-xs text-gray-500 mt-1">Loading utilities...</p>
            )}
            {selectedDetail?.ami_notes && (
              <p className="mt-2 text-xs text-gray-600" data-testid="utility-notes">
                {selectedDetail.ami_notes}
              </p>
            )}
          </div>

          {selectedValue === OTHER_VALUE && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Utility Name
              </label>
              <UtilityNameSuggest
                value={project.ami_data_provider || ''}
                projectState={projectState}
                disabled={isReadOnly}
                onTextChange={(text) => onChange({ ami_data_provider: text })}
                onPick={(u) => handleUtilityChange(u.id, u)}
                onSearch={runSearch}
              />
              <p className="text-xs text-gray-500 mt-1">
                Keep typing — if TrueLoad knows this utility it will offer it, and taking the
                offer keeps its download steps and data request.
              </p>
            </div>
          )}

          {selectedUtilityName === 'Burlington Electric Department' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer ID{' '}
                  <span className="text-gray-400 font-normal">(optional)</span>
                  <Tooltip text="Optional. Found on the customer's electric bill before the dash. Helps Burlington Electric Department locate the account, but Location ID is more important for AMI data requests." />
                </label>
                <input
                  type="text"
                  value={project.customer_id || ''}
                  onChange={(e) => onChange({ customer_id: e.target.value || null })}
                  placeholder="e.g. 123456"
                  disabled={isReadOnly}
                  data-testid="bed-customer-id"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location ID{' '}
                  <span className="text-gray-400 font-normal">(optional)</span>
                  <Tooltip text="Optional but strongly encouraged. Found on the customer's electric bill after the dash. Burlington Electric Department uses Location ID to route AMI data requests to the correct meter — without it, the request may be delayed." />
                </label>
                <input
                  type="text"
                  value={project.location_id || ''}
                  onChange={(e) => onChange({ location_id: e.target.value || null })}
                  placeholder="e.g. 4521"
                  disabled={isReadOnly}
                  data-testid="bed-location-id"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            </>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Utility Account Number{' '}
                <span className="text-gray-400 font-normal">(optional)</span>
                <Tooltip text="Optional but strongly recommended. The account number ensures the utility can locate the correct meter record. Find it on the customer's electric bill. If entered, it will auto-populate into the AMI data request." />
              </label>
              <input
                type="text"
                value={project.utility_account_number || ''}
                onChange={(e) => onChange({ utility_account_number: e.target.value })}
                placeholder="e.g. 123456789"
                disabled={isReadOnly}
                data-testid="utility-account-number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
